import type { DocPageContent } from '../types';

/**
 * "Impact & the interaction map" — Dashboard section (ADR-0074).
 *
 * Two analytics surfaces on one page. They answer the same question from
 * opposite directions — Impact says what the assistant was worth, the map says
 * where in the product it was used and where it was not — and a reader looking
 * at one almost always wants the other next.
 *
 * Reconciled from apps/web `features/impact` + `features/map` (routes `/impact`,
 * `/map`) and the `insights.*` / `map.*` catalogs. The AI-provenance caveat the
 * product itself shows is repeated here rather than dropped: a number a reader
 * trusts more than the product does is worse than no number.
 */
export const DASHBOARD_ANALYTICS_PAGE: DocPageContent = {
  slug: 'dashboard/analytics',
  titleKey: 'docs.dashboard.analytics.title',
  descriptionKey: 'docs.dashboard.analytics.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.analytics.lead' },

    { kind: 'heading', level: 2, id: 'impact', textKey: 'docs.dashboard.analytics.impact.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.analytics.impact.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.analytics.impact.item.outcomes',
        'docs.dashboard.analytics.impact.item.gaps',
        'docs.dashboard.analytics.impact.item.roi',
        'docs.dashboard.analytics.impact.item.share',
      ],
    },
    {
      kind: 'callout',
      tone: 'caution',
      titleKey: 'docs.dashboard.analytics.impact.ai.title',
      textKey: 'docs.dashboard.analytics.impact.ai.body',
    },

    { kind: 'heading', level: 2, id: 'map', textKey: 'docs.dashboard.analytics.map.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.analytics.map.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.analytics.map.item.nodes',
        'docs.dashboard.analytics.map.item.weight',
        'docs.dashboard.analytics.map.item.inspect',
        'docs.dashboard.analytics.map.item.table',
      ],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'findings',
      textKey: 'docs.dashboard.analytics.findings.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.analytics.findings.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.analytics.findings.item.uncalled',
        'docs.dashboard.analytics.findings.item.blind',
        'docs.dashboard.analytics.findings.item.gap',
        'docs.dashboard.analytics.findings.item.risky',
      ],
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.dashboard.analytics.next.roi.title',
          textKey: 'docs.dashboard.analytics.next.roi.text',
          to: { docSlug: 'guides/roi-methodology' },
        },
        {
          titleKey: 'docs.dashboard.analytics.next.convlogs.title',
          textKey: 'docs.dashboard.analytics.next.convlogs.text',
          to: { docSlug: 'dashboard/conversation-logs' },
        },
        {
          titleKey: 'docs.dashboard.analytics.next.catalog.title',
          textKey: 'docs.dashboard.analytics.next.catalog.text',
          to: { docSlug: 'dashboard/catalog' },
        },
      ],
    },
  ],
};
